import { createSlice } from '@reduxjs/toolkit';
import { DynamoConnector } from '../../DynamoConnector';

/**
 * Upgrades slice — backs the deck Upgrade Finder.
 *
 * The store holds two distinct query surfaces:
 *
 * 1. **Card-category search** (`list` / `nextCursor` / `legacyError`) used
 *    by the "By Role" mode. Drives `/upgrades?id=…&category=…[+filters]`
 *    against the unified base-search overhaul (source=card_category). The
 *    surface accepts the full new filter set — oracle full-text, qualifier
 *    clauses, effect/action/zone/flag filters, qualifiers, pip ranges,
 *    cmc/price ranges, combo-completion, multi-category, etc. Only
 *    `fetchUpgradesListByCategory` writes here.
 *
 * 2. **Source-based commander-tuned queries** (`items` / `cursor` / `status`
 *    / `source` / …) used by every other mode (staples, synergy_*,
 *    manabase, archetype, category_role, combos, top_synergies). Drives
 *    `/upgrades?id=…&source=<enum>…`. Only `fetchUpgrades` writes here.
 *
 * Match-detail (per-(cardId, category) full-match list) is a third
 * surface, populated by `fetchMatchDetail`, used for the "See all
 * matches" affordance on a card whose primary response had
 * `matchesTruncated=true`.
 */

const initialState = {
    // Card-category search state.
    categories: [],
    list: [],
    isFetching: false,
    loadingState: 'idle',
    nextCursor: null,
    legacyTotal: 0,
    legacyError: null,
    legacyErrorKind: null, // 'kill_switch' | 'timeout' | 'invalid_cursor' | 'generic'
    legacyRetryAfter: null,
    selectedListKey: null,
    selectedListIndex: -1,
    forceSkipAutoLoad: false,
    hasDoneFirstLoad: false,

    // Source-based commander-tuned state.
    items: [],
    cursor: null,
    total: 0,
    status: 'idle',
    binKey: null,
    deckCount: 0,
    source: null,
    note: null,
    isFetchingSource: false,
    sourceLoadingState: 'idle',
    hasDoneFirstSourceLoad: false,
    error: null,

    // Match-detail state — keyed by `${cardId}::${category}`.
    matchDetail: {},
};

const matchDetailKey = (cardId, category) => `${cardId || ''}::${category || ''}`;

const classifyLegacyError = (response) => {
    const code = response?.error;
    if (response?.status === 400 && code === 'invalid_cursor') return 'invalid_cursor';
    if (response?.status === 503 || code === 'card_category_upgrades_disabled') return 'kill_switch';
    if (response?.status === 504 || code === 'upgrades_timeout') return 'timeout';
    return 'generic';
};

export const upgradesSlice = createSlice({
    name: 'upgrades',
    initialState,
    reducers: {
        emptyUpgradesList: (state, action) => {
            state.list = action.payload.map((item) => ({
                ...item,
                key: item?.id,
                storeKey: 'upgradesList',
            }));
        },
        setUpgradesList: (state, action) => {
            const { Items, LastEvaluatedKey, cursor, Total, isReload } = action.payload;

            let i = -1;
            const _list =
                (Items || [])
                    .filter((item) => item !== null)
                    .map((item) => {
                        i += 1;
                        // Hydrate every item with the redux keys our list
                        // renderers depend on. cardId is the canonical id
                        // on the new contract; fall back to legacy `id`.
                        return {
                            ...item,
                            id: item?.id || item?.cardId,
                            cardId: item?.cardId || item?.id,
                            key: item?.cardId || item?.id,
                            storeKey: 'upgradesList',
                            indexPrefix: 'upgradesList_',
                            index: i + state?.list?.length || 0,
                        };
                    })
                    ?.sort((a, b) => parseFloat(b.score) - parseFloat(a.score)) || [];

            if (!isReload) {
                state.list = state.list.concat(_list);
            } else {
                state.selectedListKey = null;
                state.selectedListIndex = -1;
                state.list = _list;
            }

            // New contract: `cursor` is the opaque string; `LastEvaluatedKey`
            // is kept as a duplicate of the same value for legacy
            // compatibility. Prefer cursor when present.
            state.nextCursor = cursor ?? LastEvaluatedKey ?? null;
            state.legacyTotal = typeof Total === 'number' ? Total : state.legacyTotal;
            state.legacyError = null;
            state.legacyErrorKind = null;
            state.legacyRetryAfter = null;
            state.isFetching = false;
            state.loadingState = 'idle';
            state.hasDoneFirstLoad = true;
        },
        setUpgradesFetchingTrue: (state, action) => {
            state.loadingState = action.payload.loadingState;
            state.isFetching = true;

            if (action?.payload?.isReload) {
                state.categories = [];
                state.list = [];
                // Reset cursor only on a full reload — clearing it on
                // every fetch start would also drop it for paginating
                // load-more, which makes the "Show more picks" button
                // vanish before its busy state can render.
                state.nextCursor = null;
                state.legacyTotal = 0;
                state.legacyError = null;
                state.legacyErrorKind = null;
                state.legacyRetryAfter = null;
            }
        },
        setUpgradesError: (state, action) => {
            // The pane component handles the kind-specific behavior
            // (invalid_cursor → auto-restart, kill_switch → suppress
            // retries until retryAfter elapses, timeout → manual retry).
            // The slice just records the error shape.
            const { message, kind, retryAfter } = action.payload || {};
            state.legacyError = message || 'Request failed';
            state.legacyErrorKind = kind || 'generic';
            state.legacyRetryAfter = typeof retryAfter === 'number' ? retryAfter : null;
            state.isFetching = false;
            state.loadingState = 'idle';
            state.hasDoneFirstLoad = true;
            if (kind === 'invalid_cursor') {
                // Stored cursor crossed a deploy boundary — drop it so the
                // pane can restart from page 1.
                state.nextCursor = null;
            }
        },
        resetUpgrades: (state) => {
            state.categories = [];
            state.list = [];
            state.nextCursor = null;
            state.legacyTotal = 0;
            state.legacyError = null;
            state.legacyErrorKind = null;
            state.legacyRetryAfter = null;
            state.hasDoneFirstLoad = false;
            state.items = [];
            state.cursor = null;
            state.total = 0;
            state.status = 'idle';
            state.binKey = null;
            state.deckCount = 0;
            state.source = null;
            state.note = null;
            state.isFetchingSource = false;
            state.sourceLoadingState = 'idle';
            state.hasDoneFirstSourceLoad = false;
            state.error = null;
            state.matchDetail = {};
        },

        // Source-based (unchanged) reducers.
        setSourceFetching: (state, action) => {
            const { loadingState, isReload } = action.payload || {};
            state.isFetchingSource = true;
            state.sourceLoadingState = loadingState || 'loading';
            state.error = null;
            if (isReload) {
                state.items = [];
                state.cursor = null;
                state.total = 0;
                state.note = null;
                state.hasDoneFirstSourceLoad = false;
            }
        },
        setSourceResults: (state, action) => {
            const { response, isReload } = action.payload || {};
            const rawItems = Array.isArray(response?.Items) ? response.Items : [];
            let i = -1;
            const hydrated = rawItems
                .filter((item) => item != null)
                .map((item) => {
                    i += 1;
                    return {
                        ...item,
                        key: item?.cardId || item?.comboId || item?.key || `${i}`,
                        storeKey: 'upgradesItems',
                        indexPrefix: 'upgradesItems_',
                        index: i + (isReload ? 0 : state.items.length),
                    };
                });

            if (isReload) {
                state.items = hydrated;
            } else {
                state.items = state.items.concat(hydrated);
            }

            state.cursor = response?.cursor ?? null;
            state.total = response?.Total ?? state.items.length;
            state.status = response?.status ?? 'ready';
            state.binKey = response?.binKey ?? null;
            state.deckCount = response?.deckCount ?? 0;
            state.source = response?.source ?? null;
            state.note = response?.note ?? null;
            state.isFetchingSource = false;
            state.sourceLoadingState = 'idle';
            state.hasDoneFirstSourceLoad = true;
            state.error = null;
        },
        setSourceError: (state, action) => {
            state.error = action.payload?.message || 'Request failed';
            state.isFetchingSource = false;
            state.sourceLoadingState = 'idle';
            state.hasDoneFirstSourceLoad = true;
        },

        // Match-detail reducers.
        setMatchDetailFetching: (state, action) => {
            const { cardId, category, isReload } = action.payload || {};
            const key = matchDetailKey(cardId, category);
            const existing = state.matchDetail[key] || { items: [], cursor: null, matchCount: 0 };
            state.matchDetail[key] = {
                ...existing,
                cardId,
                category,
                isFetching: true,
                error: null,
                items: isReload ? [] : existing.items,
                cursor: isReload ? null : existing.cursor,
                hasLoaded: existing.hasLoaded && !isReload,
            };
        },
        setMatchDetailResults: (state, action) => {
            const { cardId, category, response, isReload } = action.payload || {};
            const key = matchDetailKey(cardId, category);
            const existing = state.matchDetail[key] || { items: [] };
            const incoming = Array.isArray(response?.Items) ? response.Items : [];
            state.matchDetail[key] = {
                cardId,
                category,
                items: isReload ? incoming : (existing.items || []).concat(incoming),
                cursor: response?.cursor ?? null,
                matchCount: response?.matchCount ?? incoming.length,
                isFetching: false,
                hasLoaded: true,
                error: null,
            };
        },
        setMatchDetailError: (state, action) => {
            const { cardId, category, message, kind, retryAfter } = action.payload || {};
            const key = matchDetailKey(cardId, category);
            const existing = state.matchDetail[key] || { items: [] };
            state.matchDetail[key] = {
                ...existing,
                cardId,
                category,
                isFetching: false,
                hasLoaded: true,
                error: message || 'Request failed',
                errorKind: kind || 'generic',
                retryAfter: typeof retryAfter === 'number' ? retryAfter : null,
            };
        },
    },
});

export const {
    resetUpgrades,
    setUpgradesList,
    emptyUpgradesList,
    setUpgradesFetchingTrue,
    setUpgradesError,
    setSourceFetching,
    setSourceResults,
    setSourceError,
    setMatchDetailFetching,
    setMatchDetailResults,
    setMatchDetailError,
} = upgradesSlice.actions;

/**
 * Card-category search. Hits `/upgrades?id=…&category=…[+filters]` against
 * the unified base-search endpoint (source=card_category). Writes into
 * `state.upgrades.list`.
 *
 * `params` carries every accepted filter — id/category are required, all
 * others optional. `isReload` resets pagination; otherwise the prior
 * `nextCursor` is appended automatically by the caller (the pane).
 *
 * Errors are classified into one of: invalid_cursor, kill_switch,
 * timeout, generic — and stored on the slice for the pane to surface.
 */
export const fetchUpgradesListByCategory =
    (params = {}, isReload = false) =>
    (dispatch) => {
        const { isReload: _ignored, ...transportParams } = params;
        dispatch(
            setUpgradesFetchingTrue({
                loadingState: transportParams?.cursor ? 'loadingMore' : 'loading',
                isReload,
            })
        );

        if (isReload) {
            dispatch(emptyUpgradesList([]));
        }

        DynamoConnector.getDeckUpgrades(transportParams, (results) => {
            if (results?.error) {
                const kind = classifyLegacyError(results);
                dispatch(
                    setUpgradesError({
                        message: results.message || results.error,
                        kind,
                        retryAfter: results.retryAfter,
                    })
                );
                return;
            }
            dispatch(setUpgradesList({ ...results, isReload }));
        });
    };

/**
 * Source-based commander-tuned fetch. Drives every non-card_category mode
 * of the Upgrade Finder (staples, synergy_*, manabase, archetype,
 * category_role, combos, top_synergies). Callers pass the full params
 * object — see `upgrades-api-additions.md` for the per-source param shape.
 *
 * `isReload` should be true on every filter change so pagination resets;
 * paginate by re-calling with the returned `cursor`.
 */
export const fetchUpgrades =
    (params = {}, isReload = true) =>
    (dispatch) => {
        dispatch(
            setSourceFetching({
                loadingState: params?.cursor ? 'loadingMore' : 'loading',
                isReload,
            })
        );

        DynamoConnector.getDeckUpgrades(params, (response) => {
            if (response?.error) {
                dispatch(setSourceError({ message: response.message || response.error }));
                return;
            }
            dispatch(setSourceResults({ response, isReload }));
        });
    };

/**
 * Per-(cardId, category) full-match-list fetch. Drives the "See all
 * matches" affordance on a card whose primary `/upgrades` response had
 * `matchesTruncated=true`. Pages via `cursor`.
 */
export const fetchMatchDetail =
    ({ cardId, category, cursor, limit, isReload = true } = {}) =>
    (dispatch) => {
        if (!cardId || !category) return;
        dispatch(setMatchDetailFetching({ cardId, category, isReload }));
        const params = { id: cardId, category };
        if (cursor) params.cursor = cursor;
        if (typeof limit === 'number') params.limit = limit;
        DynamoConnector.getUpgradeMatchDetail(params, (response) => {
            if (response?.error) {
                dispatch(
                    setMatchDetailError({
                        cardId,
                        category,
                        message: response.message || response.error,
                        kind: classifyLegacyError(response),
                        retryAfter: response.retryAfter,
                    })
                );
                return;
            }
            dispatch(setMatchDetailResults({ cardId, category, response, isReload }));
        });
    };

export default upgradesSlice.reducer;
